import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismBreadcrumb,
  PrismButton,
  PrismCard,
  PrismRadioGroup,
} from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { and, not, or } from 'ember-truth-helpers';
import PaymentPaymentMethodRow from 'frontend-payments/components/payment/payment-method-row';
import type {
  Account,
  PaymentDetail,
  Transaction,
} from 'frontend-payments/models';
import type {
  BankDetails,
  CheckDetails,
} from 'frontend-payments/models/payment-detail';
import {
  PAYMENT_METHOD_BANK_TRANSFER,
  PAYMENT_METHOD_CHECK,
  PAYMENT_METHOD_CREDIT_CARD,
} from 'frontend-payments/utils/constants/payment-method-constants';
import { downloadPdf } from 'frontend-payments/utils/download-pdf';
import { openUrl } from 'frontend-utils/utils/url/open-url';

import styles from './method-selection.module.css';

type PaymentMethod = 'credit_card' | 'check' | 'bank_transfer';

interface PaymentMethodSelectionSignature {
  Args: {
    account: Account | null;
    isAccountLoading: boolean;
    onBack: () => void;
    paymentDetails: PaymentDetail | null;
    transactions: Transaction[];
  };
}

export default class PaymentMethodSelection extends Component<PaymentMethodSelectionSignature> {
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare session: Services['session'];
  @service declare toast: Services['toast'];

  @tracked selectedPaymentMethod: PaymentMethod | undefined =
    PAYMENT_METHOD_CREDIT_CARD;

  downloadInvoiceTask = task(
    async (downloadUrl: string, filename: string): Promise<void> => {
      try {
        await downloadPdf(downloadUrl, filename);
      } catch (error) {
        this.errorTracking.captureException(error);
        this.toast.error(
          this.intl.t('payments.invoice.table.download_error_message'),
          {
            title: this.intl.t('payments.invoice.table.download_error_title'),
            showIcon: true,
          },
        );
      }
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get availablePaymentMethods() {
    if (!this.args.account || this.args.isAccountLoading) {
      return [];
    }
    return this.args.account.payments.availablePaymentMethods;
  }

  get bankDetails(): BankDetails | null {
    return this.args.paymentDetails?.bankDetails ?? null;
  }

  get checkDetails(): CheckDetails | null {
    return this.args.paymentDetails?.checkDetails ?? null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get detailsTitle() {
    if (this.isCheckPaymentMethodSelected) {
      return this.intl.t('payments.method_selection.details.check');
    }

    if (this.isBankTransferPaymentMethodSelected) {
      return this.intl.t('payments.method_selection.details.bank_transfer');
    }

    return '';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTransactionsTotalAmount() {
    return (
      this.intl.formatNumber(this.transactionsTotalAmount, {
        style: 'currency',
        currency: this.primaryTransaction?.currency ?? 'USD',
      }) ?? this.intl.t('general.missing')
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasMultipleTransactions() {
    return this.args.transactions.length > 1;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isBankTransferPaymentMethodSelected() {
    return this.selectedPaymentMethod === PAYMENT_METHOD_BANK_TRANSFER;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isCheckPaymentMethodSelected() {
    return this.selectedPaymentMethod === PAYMENT_METHOD_CHECK;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isCreditCardOrDirectDebitPaymentMethodSelected() {
    return this.selectedPaymentMethod === PAYMENT_METHOD_CREDIT_CARD;
  }

  get primaryTransaction(): Transaction | undefined {
    return this.args.transactions[0];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get shouldShowInvoiceButton() {
    return (
      !this.hasMultipleTransactions && Boolean(this.primaryTransaction?.pdfUrl)
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get transactionsTotalAmount() {
    return this.args.transactions.reduce(
      (total, transaction) => total + (transaction.totalAmount ?? 0),
      0,
    );
  }

  @action
  downloadInvoice(): void {
    if (!this.primaryTransaction?.pdfUrl) {
      return;
    }

    const filename = `${this.primaryTransaction.transactionType}-${this.primaryTransaction.transactionNumber}.pdf`;
    void this.downloadInvoiceTask.perform(
      this.primaryTransaction.pdfUrl,
      filename,
    );
  }

  @action
  onBackClick(): void {
    this.args.onBack();
  }

  @action
  onPaymentMethodChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.selectedPaymentMethod = target.value as PaymentMethod;
  }

  @action
  onPayNowClick(): void {
    // TODO: Handle triggering the payment url for multiple transactions
    if (!this.primaryTransaction?.paymentUrl) {
      this.toast.error(
        this.intl.t('payments.payment_method_selection.error.no_payment_url'),
        {
          showIcon: true,
        },
      );
      return;
    }

    // Redirect to the payment URL in a new tab
    openUrl(this.primaryTransaction.paymentUrl);
    this.session.openPayByLink();
  }

  <template>
    <div class={{styles.container}}>
      <section>
        <div class="mb-100">
          <PrismBreadcrumb as |B|>
            <B.item
              @label={{t "payments.app.header.overview"}}
              @route="authenticated.overview"
            />
            <B.item
              @isCurrent={{true}}
              @label={{t "payments.payment_details.payment_method"}}
            />
          </PrismBreadcrumb>
        </div>

        <div class="mb-600 flex items-center gap-100">
          {{#if this.hasMultipleTransactions}}
            <h2 class="text-emphasis">
              {{t
                "payments.method_selection.title_multiple"
                invoiceAmount=this.formattedTransactionsTotalAmount
                transactionsAmount=@transactions.length
              }}
            </h2>
          {{else}}
            <h2 class="text-emphasis">
              {{t
                "payments.method_selection.title"
                invoiceAmount=this.primaryTransaction.invoiceTotal
                invoiceNumber=this.primaryTransaction.transactionNumber
              }}
            </h2>
          {{/if}}

          {{#if this.shouldShowInvoiceButton}}
            <PrismButton
              @buttonType="secondary"
              @iconBefore="download"
              @isDisabled={{this.downloadInvoiceTask.isRunning}}
              @isLoading={{this.downloadInvoiceTask.isRunning}}
              @label={{t "payments.invoice.table.header.invoice"}}
              @onClick={{this.downloadInvoice}}
              @size="small"
            />
          {{/if}}
        </div>

        <div class="flex gap-600">
          <div class="w-[600px] min-w-[600px]">
            <PrismCard>
              <:header as |Header|>
                <Header.title>{{t
                    "payments.method_selection.choose_payment_method"
                  }}</Header.title>
              </:header>
              <:content>
                <PrismRadioGroup
                  @name="payment-method"
                  @onChange={{this.onPaymentMethodChange}}
                  @value={{this.selectedPaymentMethod}}
                  as |RadioGroup|
                >
                  <RadioGroup.input @value="credit_card">
                    {{t "payments.method_selection.credict_card_direct_debit"}}
                  </RadioGroup.input>
                  {{#if this.checkDetails}}
                    <RadioGroup.input @value="check">
                      {{t "payments.method_selection.check"}}
                    </RadioGroup.input>
                  {{/if}}
                  {{#if this.bankDetails}}
                    <RadioGroup.input @value="bank_transfer">
                      {{t "payments.method_selection.bank_transfer"}}
                    </RadioGroup.input>
                  {{/if}}
                </PrismRadioGroup>
              </:content>

              <:footer>
                {{#if this.isCreditCardOrDirectDebitPaymentMethodSelected}}
                  <div class="flex justify-end">
                    <PrismButton
                      @buttonType="primary"
                      @isDisabled={{not this.selectedPaymentMethod}}
                      @label={{t "payments.pay_now"}}
                      @onClick={{this.onPayNowClick}}
                    />
                  </div>
                {{/if}}
              </:footer>
            </PrismCard>
          </div>

          {{#if
            (or
              this.isCheckPaymentMethodSelected
              this.isBankTransferPaymentMethodSelected
            )
          }}
            <div class="w-full">
              <PrismCard>
                <:header as |Header|>
                  <Header.title>{{this.detailsTitle}}</Header.title>
                </:header>
                <:content>
                  {{#if
                    (and this.isCheckPaymentMethodSelected this.checkDetails)
                  }}
                    <PaymentPaymentMethodRow
                      @label={{t "payments.method_selection.details.bank_name"}}
                      @value={{this.checkDetails.bankName}}
                    />
                    <PaymentPaymentMethodRow
                      @label={{t "payments.method_selection.details.address"}}
                      @value={{this.checkDetails.address}}
                    />
                  {{/if}}
                  {{#if
                    (and
                      this.isBankTransferPaymentMethodSelected this.bankDetails
                    )
                  }}
                    <PaymentPaymentMethodRow
                      @label={{t "payments.method_selection.details.name"}}
                      @value={{this.bankDetails.bankName}}
                    />
                    {{#if this.bankDetails.accountNumber}}
                      <PaymentPaymentMethodRow
                        @label={{t
                          "payments.method_selection.details.account_no"
                        }}
                        @value={{this.bankDetails.accountNumber}}
                      />
                    {{/if}}

                    {{#if this.bankDetails.routingNumber}}
                      <PaymentPaymentMethodRow
                        @label={{t
                          "payments.method_selection.details.routing_number"
                        }}
                        @value={{this.bankDetails.routingNumber}}
                      />
                    {{/if}}
                    <PaymentPaymentMethodRow
                      @label={{t "payments.method_selection.details.iban"}}
                      @value={{this.bankDetails.iban}}
                    />
                    <PaymentPaymentMethodRow
                      @label={{t "payments.method_selection.details.swift"}}
                      @value={{this.bankDetails.swiftCode}}
                    />
                    <PaymentPaymentMethodRow
                      @label={{t "payments.method_selection.details.address"}}
                      @value={{this.bankDetails.address}}
                    />
                    <div class="mt-600">
                      <PaymentPaymentMethodRow
                        @label={{t
                          "payments.method_selection.details.payment_description"
                        }}
                        @value={{this.primaryTransaction.transactionNumber}}
                      />
                    </div>
                  {{/if}}
                </:content>
              </PrismCard>
            </div>
          {{/if}}
        </div>
      </section>
    </div>
  </template>
}
